import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { TAddResourcesToGroupInputSchema } from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group.validation';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { TGroupResourceItem } from '@/shared/features/groups/context/groups-config-context.types';
import { showToastError } from '@/shared/utils/toast';

export const useGroupResourceActions = () => {
  const { t } = useTranslation();
  const { createResourceUpdatePromises } =
    useGroupsConfig().useResourceUpdatePromises();

  const moveResourcesToGroup = async (
    resources: TGroupResourceItem[],
    groupId: string,
    onSuccess?: () => void,
    onClose?: () => void,
  ) => {
    // An empty target is the tree root: that lifts a group out, adding nothing.
    const isRootMove = !groupId;
    const count = resources.length;

    try {
      const promises = createResourceUpdatePromises(resources, groupId);
      // An unrecognised resource gets no update; that must not report success.
      if (promises.length !== resources.length) {
        throw new Error('A resource has no group update');
      }
      await Promise.all(promises);
      toast.success(
        isRootMove
          ? t('groups:drawer.update_group.update_success')
          : t('groups:add_resources_to_group_success', { count }),
      );
      onSuccess?.();
      onClose?.();
    } catch {
      showToastError(
        isRootMove
          ? t('groups:drawer.update_group.update_error')
          : t('groups:add_resources_to_group_error', { count }),
      );
    }
  };

  const handleAddResourcesToGroup = async (
    data: TSubmittedData<TAddResourcesToGroupInputSchema>,
    resources: TGroupResourceItem[],
    onSuccess?: () => void,
    onClose?: () => void,
  ) => moveResourcesToGroup(resources, data.data.groupId, onSuccess, onClose);

  return {
    createResourceUpdatePromises,
    handleAddResourcesToGroup,
    moveResourcesToGroup,
  };
};
